import React, { useState } from 'react';

import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { useAuth } from 'context/auth-context';
import useAthletes from 'hooks/athlete/useAthletes';
import useWaiverCandidates from 'hooks/athlete/useWaiverCandidates';
import SimpleHeaderWithCount from 'layouts/MobileLayout/components/SimpleHeaderWithCount';
import DrawerAthleteForm from 'pages/private/owner/Athletes/components/DrawerAthleteForm';
import SendWaiverBanner from 'pages/private/owner/Athletes/components/SendWaiverBanner';
import SendWaiverModal from 'pages/private/owner/Athletes/components/SendWaiverModal';
import { AthleteList } from 'types';

import AthletesPageHeader from './components/AthletesPageHeader';
import AthletesTableContainer from './components/AthletesTableContainer';
import FiltersDrawer from './components/FiltersDrawer';

interface FilterTerms {
  searchTerm: string;
  isArrangeByLastName: boolean;
  athleteStatus: string;
  loginEnabled: string;
  packages: string[];
  hasPaymentMethod: string;
  hasSignedWaiver: string;
}

const Athletes = (): JSX.Element => {
  const initialSearchFiltersValue = {
    searchTerm: '',
    isArrangeByLastName: false,
    athleteStatus: '',
    loginEnabled: '',
    packages: [],
    hasPaymentMethod: '',
    hasSignedWaiver: '',
  };

  const { hasPerm } = useAuth();
  const athletesPerm = hasPerm('AthleteMutate');
  const canSendWaivers = hasPerm('IssuedDocumentMutate');

  const [isCreateFormOpened, { open, close }] = useDisclosure(false);
  const [isFiltersOpened, { open: openFilters, close: closeFilters }] = useDisclosure(false);
  const [isSendWaiverModalOpen, { open: openSendWaiverModal, close: closeSendWaiverModal }] =
    useDisclosure(false);

  const [searchFilters, setSearchFilters] = useState<FilterTerms>(initialSearchFiltersValue);
  const [debouncedSearchFilters] = useDebouncedValue<FilterTerms>(searchFilters, 250);
  const [tableData, setTableData] = useState<AthleteList[]>([]);
  const [orderingValue, setOrderingValue] = useState<string>('firstName');

  /* Get count for SimpleHeader. React Query will deduplicate with table's call (same query key) */
  const { data, isLoading, isError } = useAthletes({
    ...debouncedSearchFilters,
    ordering: orderingValue,
  });

  const athleteCount = data?.pages?.[0]?.data?.count;

  const isNoWaiverFilterActive = canSendWaivers && debouncedSearchFilters.hasSignedWaiver === 'false';
  const { data: waiverCandidates } = useWaiverCandidates(debouncedSearchFilters, {
    enabled: isNoWaiverFilterActive,
  });

  return (
    <>
      <SimpleHeaderWithCount title='Athletes' count={athleteCount} isLoading={isLoading} isError={isError} />
      <div className='content-no-padding'>
        <AthletesPageHeader
          searchFilters={searchFilters}
          setSearchFilters={setSearchFilters}
          handleOpenCreateForm={open}
          handleOpenFilters={openFilters}
          permission={athletesPerm}
        />
        {isNoWaiverFilterActive && waiverCandidates && (
          <SendWaiverBanner count={waiverCandidates.length} onReview={openSendWaiverModal} />
        )}
        <AthletesTableContainer
          tableData={tableData}
          setTableData={setTableData}
          searchFilters={debouncedSearchFilters}
          orderingValue={orderingValue}
          setOrderingValue={setOrderingValue}
          permission={athletesPerm}
        />
        <DrawerAthleteForm type='create' opened={isCreateFormOpened} closeHandler={close} />
        <FiltersDrawer
          opened={isFiltersOpened}
          initialSearchFiltersValue={initialSearchFiltersValue}
          searchFilters={searchFilters}
          setSearchFilters={setSearchFilters}
          closeFilters={closeFilters}
        />
        <SendWaiverModal
          opened={isSendWaiverModalOpen}
          onClose={closeSendWaiverModal}
          filters={debouncedSearchFilters}
          fullScreen
          showTemplatesLink={false}
        />
      </div>
    </>
  );
};

export default Athletes;
